import { useCallback, useEffect, useState } from "react";
import {
  TREATMENT_START_DATE_PRECISIONS,
  TREATMENT_START_SOURCES,
  type EmployeeTreatmentStart,
  type TreatmentStartDatePrecision,
  type TreatmentStartSource,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { UnauthorizedError } from "@/lib/clinician-api";
import {
  getEarlierTreatmentStart,
  recordEarlierTreatmentStart,
} from "@/lib/earlier-treatment-start-api";
import {
  estimateLine,
  PRECISION_LABEL,
  recordLine,
  SOURCE_LABEL,
  startDateInput,
  startDatePayload,
  underhallBlockedText,
} from "@/lib/earlier-treatment-start";
import { useReadOnly } from "./SupportView";

// TES-1073 (Ö-297, Ö-298) — the documented earlier treatment start on the
// patient page, for any patient. Emelie: the source is F-07, an already
// saved start date, or Kirill's own registration (e.g. after a meeting).
//
// The doctor registers it: the date, how precise it is, where it comes
// from, and for an estimate his explicit confirmation that it shows at least
// twelve full months. An admin in her support view reads it; the form is
// hidden there (TES-1040: hidden, not disabled). It is prefilled from the
// transfer approval's verified start or her F-07 date when nothing is
// registered; the precision of an F-07 date is left for him to choose.

// Fired after a registration is saved, so a plan picker elsewhere on the
// page re-reads the rule without the page threading a reload key through.
const SAVED_EVENT = "cbe:earlier-treatment-start-saved";

/** "Ella Underhåll"'s treatment rule for this patient; null while loading. */
export function useUnderhallEligibility(
  patientId: string,
): EmployeeTreatmentStart["underhall"] | null {
  const [underhall, setUnderhall] = useState<
    EmployeeTreatmentStart["underhall"] | null
  >(null);
  useEffect(() => {
    let live = true;
    const load = () =>
      void getEarlierTreatmentStart(patientId)
        .then((r) => {
          if (live) setUnderhall(r.underhall);
        })
        // Unknown is not eligible: the server refuses "Underhåll" anyway, and a
        // picker that offered it on a failed read would offer a dead end.
        .catch(() => {
          if (live)
            setUnderhall({
              status: "not_eligible",
              reasons: [],
              eligibleFrom: null,
            });
        });
    const onSaved = (e: Event) => {
      if ((e as CustomEvent<string>).detail === patientId) load();
    };
    load();
    window.addEventListener(SAVED_EVENT, onSaved);
    return () => {
      live = false;
      window.removeEventListener(SAVED_EVENT, onSaved);
    };
  }, [patientId]);
  return underhall;
}

export function PatientEarlierTreatmentStart({
  patientId,
}: {
  patientId: string;
}) {
  const { claims, signOut } = useAuth();
  const readOnly = useReadOnly();
  const canWrite = !readOnly && claims?.role === "doctor";

  const [data, setData] = useState<EmployeeTreatmentStart | null>(null);
  const [editing, setEditing] = useState(false);
  const [precision, setPrecision] =
    useState<TreatmentStartDatePrecision | null>(null);
  const [date, setDate] = useState("");
  const [source, setSource] = useState<TreatmentStartSource | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fill = useCallback((d: EmployeeTreatmentStart) => {
    setData(d);
    const from = d.record ?? d.prefill;
    const p = from?.datePrecision ?? null;
    setPrecision(p);
    setDate(from ? startDateInput(from.treatmentStartedAt, p ?? "exact") : "");
    setSource(from?.source ?? null);
    setConfirmed(false);
  }, []);

  useEffect(() => {
    getEarlierTreatmentStart(patientId)
      .then(fill)
      .catch((e: unknown) => {
        if (e instanceof UnauthorizedError) void signOut();
        else setError("Behandlingsstarten kunde inte hämtas.");
      });
  }, [patientId, fill, signOut]);

  const payloadDate = precision ? startDatePayload(date, precision) : null;
  const ready = precision !== null && source !== null && payloadDate !== null;

  async function save() {
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      fill(
        await recordEarlierTreatmentStart(patientId, {
          treatmentStartedAt: payloadDate,
          datePrecision: precision,
          source,
          ...(precision === "estimated"
            ? { estimateConfirmed: confirmed }
            : {}),
        }),
      );
      setEditing(false);
      window.dispatchEvent(new CustomEvent(SAVED_EVENT, { detail: patientId }));
    } catch (e) {
      if (e instanceof UnauthorizedError) void signOut();
      else
        setError("Behandlingsstarten kunde inte sparas. Kontrollera fälten.");
    } finally {
      setBusy(false);
    }
  }

  const blocked = underhallBlockedText(data?.underhall ?? null);
  const record = data?.record ?? null;
  const estimate = record ? estimateLine(record) : null;

  return (
    <section
      aria-label="Tidigare behandlingsstart"
      className="mb-lg rounded-md border border-surfaceMuted bg-surface p-md"
      data-testid="earlier-treatment-start"
    >
      <h2 className="font-display text-lg mb-xs">Tidigare behandlingsstart</h2>
      {error ? <p className="text-sm text-danger">{error}</p> : null}
      {!data ? (
        !error ? (
          <p className="text-sm text-textSecondary">Hämtar…</p>
        ) : null
      ) : (
        <>
          <p className="text-sm">
            {record
              ? recordLine(record)
              : "Ingen behandlingsstart registrerad."}
          </p>
          {estimate ? (
            <p className="text-xs text-textSecondary mt-xs">{estimate}</p>
          ) : null}
          <p
            className={`text-xs mt-xs ${blocked ? "text-warning" : "text-textSecondary"}`}
            data-testid="underhall-eligibility"
          >
            {blocked ??
              "Ella Underhåll kan väljas: minst tolv månaders dokumenterad behandling."}
          </p>
        </>
      )}

      {data && canWrite && !editing ? (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="mt-sm text-xs text-textSecondary underline hover:text-textPrimary"
        >
          {record ? "Ändra behandlingsstart" : "Registrera behandlingsstart"}
        </button>
      ) : null}

      {data && canWrite && editing ? (
        <div className="mt-sm flex flex-col gap-sm rounded-md bg-surfaceMuted/40 p-sm text-sm">
          {!record && data.prefill ? (
            <p className="text-xs text-textSecondary">
              {data.prefill.origin === "transfer_approval"
                ? "Förifyllt från godkännandet av receptövertaget."
                : "Förifyllt från patientens svar i receptövertaget (F-07). Välj hur säkert datumet är."}
            </p>
          ) : null}
          <fieldset className="flex flex-wrap gap-sm">
            <legend className="text-textSecondary mb-xs">Precision</legend>
            {TREATMENT_START_DATE_PRECISIONS.map((p) => (
              <label key={p} className="flex items-center gap-xs">
                <input
                  type="radio"
                  name={`earlier-treatment-start-precision-${patientId}`}
                  checked={precision === p}
                  disabled={busy}
                  onChange={() => {
                    // Keep what was typed, reshaped for the new input.
                    const iso = precision
                      ? startDatePayload(date, precision)
                      : null;
                    setPrecision(p);
                    setDate(iso ? startDateInput(iso, p) : "");
                    if (p !== "estimated") setConfirmed(false);
                  }}
                />
                {PRECISION_LABEL[p]}
              </label>
            ))}
          </fieldset>
          {precision ? (
            <label className="flex flex-col gap-xs">
              <span className="text-textSecondary">Behandlingsstart</span>
              <input
                type={
                  precision === "year"
                    ? "number"
                    : precision === "month"
                      ? "month"
                      : "date"
                }
                min={precision === "year" ? 1990 : undefined}
                max={
                  precision === "year" ? new Date().getFullYear() : undefined
                }
                value={date}
                disabled={busy}
                onChange={(e) => setDate(e.target.value)}
                className="w-fit rounded-md border border-surfaceMuted p-xs"
              />
            </label>
          ) : null}
          <label className="flex flex-col gap-xs">
            <span className="text-textSecondary">Källa</span>
            <select
              value={source ?? ""}
              disabled={busy}
              onChange={(e) =>
                setSource(
                  (e.target.value || null) as TreatmentStartSource | null,
                )
              }
              className="w-fit rounded-md border border-surfaceMuted p-xs"
            >
              <option value="">Välj källa</option>
              {TREATMENT_START_SOURCES.map((s) => (
                <option key={s} value={s}>
                  {SOURCE_LABEL[s]}
                </option>
              ))}
            </select>
          </label>
          {precision === "estimated" ? (
            <label className="flex items-start gap-xs">
              <input
                type="checkbox"
                checked={confirmed}
                disabled={busy}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="mt-[2px]"
              />
              <span>
                Jag bekräftar att uppskattningen visar minst tolv hela månaders
                behandling. Utan bekräftelse räknas den inte.
              </span>
            </label>
          ) : null}
          <div className="flex gap-sm">
            <button
              type="button"
              onClick={() => void save()}
              disabled={busy || !ready}
              className="rounded-md bg-textPrimary px-md py-xs text-sm text-surface disabled:opacity-50"
            >
              {busy ? "Sparar…" : "Spara behandlingsstart"}
            </button>
            <button
              type="button"
              onClick={() => {
                if (data) fill(data);
                setEditing(false);
                setError(null);
              }}
              disabled={busy}
              className="rounded-md px-md py-xs text-sm text-textSecondary hover:bg-surfaceMuted"
            >
              Avbryt
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
